<template>
  <UIInputAutocomplete
    v-model="modelValue"
    :data-refid
    :data
    :disabled
    :error
    :label
    :silent
    :size
    popper-class="cashflow-projection-form-account-position"
    placeholder="Find an option"
    @ready="handleReady"
  />
</template>

<script setup lang="ts">
import { computed, inject, onMounted, ref } from 'vue'

import { CashFlowProjectionClass } from '..'
import { DropdownListData, InputSize } from '@types'

import { CASHFLOW_PROJECTION_FIELD } from '../utils/const'
import { TABLE_NAME as ANALYTICS_TABLE_NAME } from '@/store/analytics/value'
import { TABLE_NAME as ASSETS_TABLE_NAME } from '@/store/assets'

import { DuckDBService } from '@/services/duckdb'

import { UIInputAutocomplete } from '@ui/fields'

type Props = {
  dataRefid?: string
  instance: CashFlowProjectionClass
  label?: string
  size?: InputSize
  disabled?: boolean
  focusOnLoad?: boolean
}

const props = defineProps<Props>()

const duckdbInstance = DuckDBService.instance()

const data = ref<DropdownListData[]>([])
const isLoading = ref(false)

const modelValue = computed({
  get: () => {
    const accountId = props.instance.field<string>(CASHFLOW_PROJECTION_FIELD.ACCOUNT_ID).value
    const assetId = props.instance.field<string>(CASHFLOW_PROJECTION_FIELD.ASSET_ID).value
    return `${accountId}-${assetId}`
  },
  set: (value: string) => {
    const [accountId = '', assetId = ''] = value?.split('-') || []
    props.instance.field(CASHFLOW_PROJECTION_FIELD.ACCOUNT_ID).value = accountId
    props.instance.field(CASHFLOW_PROJECTION_FIELD.ASSET_ID).value = assetId
  },
})

const isSavedCashFlowProjection = inject('isSavedCashFlowProjection', ref(false))

const error = computed(() => props.instance.errors.account_id)
const silent = computed(() => !isSavedCashFlowProjection.value)
const disabled = computed(() => isLoading.value || props.disabled)

const handleReady = (el: HTMLInputElement) => {
  if (!props.focusOnLoad) return
  el.focus()
}

const fetchData = async () => {
  isLoading.value = true
  try {
    const result = await duckdbInstance.query(`
      SELECT
        DISTINCT CONCAT(acc.id, '-', ast.id) as value, CONCAT(acc.name, ' - ', ast.name) AS label
      FROM ${ANALYTICS_TABLE_NAME} AS av
      LEFT JOIN ${ASSETS_TABLE_NAME} AS acc ON acc.id = av.account_id
      LEFT JOIN ${ASSETS_TABLE_NAME} AS ast ON ast.id = av.asset_id
      ORDER BY acc.name, ast.name
    `)
    data.value = result
  } catch {
    console.error('Error fetching data')
  } finally {
    isLoading.value = false
  }
}

onMounted(fetchData)
</script>

<style>
.cashflow-projection-form-position {
  &__popper {
    .v-popper__inner {
      @apply min-w-44;
    }
  }
}
</style>
